/**
 * lib/api/onboarding.js — driver ↔ tenant first-touch bookkeeping.
 *
 * Reads/writes `lbs_driver_tenant_onboarding` (see
 * `/spec_review/100-features/034_driver_tenant_onboarding.sql`).
 *
 * Introduced 2026-07-14 as P3a-1 of the post-R7 backlog. The table
 * has existed since Feb 2026 but no UI surfaced its rows; this API
 * (plus `pages/AdminOnboardingRequests.jsx`) fixes that gap.
 */
import { supabase } from "@/lib/supabaseClient";

/** List onboarding rows for a client, joined with driver profile info. */
export async function listDriverOnboarding({ clientId }) {
  let q = supabase
    .from("lbs_driver_tenant_onboarding")
    .select(
      `id, driver_user_id, client_id, first_seen_at, welcome_sent_at, welcome_outbox_id,
       driver:lbs_user_profiles!driver_user_id(id, first_name, last_name, email, phone)`
    )
    .order("first_seen_at", { ascending: false })
    .limit(200);
  if (clientId) q = q.eq("client_id", clientId);
  return q;
}

/**
 * Re-queue the driver_welcome email for a specific onboarding row.
 * Idempotent from the queue's perspective — a new outbox row is
 * enqueued (rather than resetting the old one) so we keep an audit
 * trail of every attempted welcome.
 */
export async function resendDriverWelcome({ onboardingId, clientId, driverUserId, driverEmail }) {
  if (!driverEmail) return { error: { message: "Driver has no email address on file" } };
  const { data: outbox, error: e1 } = await supabase
    .from("lbs_email_outbox")
    .insert({
      client_id:    clientId,
      template_key: "driver_welcome",
      to_email:     driverEmail,
      status:       "pending",
      entity_type:  "driver_welcome_resend",
      entity_id:    onboardingId,
      context:      { driver_user_id: driverUserId },
    })
    .select()
    .maybeSingle();
  if (e1) return { error: e1 };
  // Mark the onboarding row so client_admins can see it was retried.
  const { error: e2 } = await supabase
    .from("lbs_driver_tenant_onboarding")
    .update({ welcome_outbox_id: outbox.id, welcome_sent_at: null })
    .eq("id", onboardingId);
  return { data: outbox, error: e2 };
}
